import React, { useState, useMemo } from 'react';
import { Card, Typography, Tag, Row, Col, Tooltip, Modal } from 'antd';
import { calculatePREVENTScore } from '../utils/riskCalculators';

const { Title, Text, Paragraph } = Typography;

const PreventScore = ({ tableData, patientDemographics }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const getParameterValue = (paramKey) => {
    if (!tableData || !Array.isArray(tableData)) return null;
    for (const categoryGroup of tableData) {
      const param = (categoryGroup.parameters || []).find(p => p.key === paramKey);
      if (param && param.currentValue !== null && param.currentValue !== undefined) {
        const numValue = parseFloat(param.currentValue);
        return isNaN(numValue) ? null : numValue;
      }
    }
    return null;
  };

  // Memoize calculations
  const preventResult = useMemo(() => {
    const currentAge = patientDemographics?.birthDate
      ? Math.floor((new Date() - new Date(patientDemographics.birthDate)) / (365.25 * 24 * 60 * 60 * 1000))
      : patientDemographics?.age || null;

    const height = getParameterValue('vitals_height'); // cm
    const weight = getParameterValue('vitals_weight'); // kg
    const bmi = (height && weight) ? weight / Math.pow(height / 100, 2) : null;

    const riskParams = {
      age: currentAge,
      sex: patientDemographics?.sex?.toLowerCase() || null,
      totalCholesterol: getParameterValue('labs_lipid_chol_total'),
      hdlCholesterol: getParameterValue('labs_lipid_hdl'),
      systolicBP: getParameterValue('vitals_sbp'),
      bmi: bmi,
      eGFR: getParameterValue('labs_renal_egfr'),
      onBPMeds: patientDemographics?.isOnBpMeds,
      diabetes: patientDemographics?.hasDiabetes || false,
      smoker: patientDemographics?.isSmoker,
    };

    return calculatePREVENTScore(riskParams);
  }, [tableData, patientDemographics]);

  // Helper function to get risk color based on percentage
  const getRiskColor = (riskPercent) => {
    if (riskPercent < 5) return '#52c41a'; // Green - Low Risk
    if (riskPercent < 7.5) return '#faad14'; // Yellow - Borderline Risk
    if (riskPercent < 20) return '#fa8c16'; // Orange - Intermediate Risk
    return '#f5222d'; // Red - High Risk
  };

  // Helper function to get risk category based on percentage
  const getRiskCategory = (riskPercent) => {
    if (riskPercent < 5) return 'Low Risk';
    if (riskPercent < 7.5) return 'Borderline';
    if (riskPercent < 20) return 'Intermediate';
    return 'High Risk';
  };

  return (
    <Card>
      <Title level={5}>PREVENT Score - 10-Year Cardiovascular Risk Assessment</Title>

      {preventResult.error ? (
        <Paragraph type="danger" style={{ minHeight: '100px' }}>
          Missing or invalid parameters: {preventResult.missing.join(', ')}
        </Paragraph>
      ) : (
        <>
          {/* Overall CVD Risk */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24}>
              <Tooltip title="Cardiovascular disease (CVD) is a general term for conditions affecting the heart or blood vessels.">
                <div 
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: `2px solid ${preventResult.color}30`,
                    cursor: 'pointer'
                  }}
                  onClick={() => setIsModalVisible(true)}
                >
                  <Text strong style={{ fontSize: '32px', color: preventResult.color }}>
                    {preventResult.score}%
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '16px' }}>Overall 10-Year CVD Risk</Text>
                  <div style={{ marginTop: '8px' }}>
                    <Tag color={preventResult.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {preventResult.category}
                    </Tag>
                  </div>
                </div>
              </Tooltip>
            </Col>
          </Row>

          {/* Specific Risk Components */}
          <Title level={5} style={{ marginBottom: '16px', color: '#1890ff' }}>
            Specific Risk Breakdown
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* ASCVD Risk */}
            <Col xs={24} sm={12} md={8}>
              <Tooltip title="Atherosclerotic Cardiovascular Disease (ASCVD) is a buildup of cholesterol plaque in the arteries.">
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  borderRadius: '6px',
                  border: `1px solid ${getRiskColor(preventResult.ascvdRisk)}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <Text strong style={{ fontSize: '24px', color: getRiskColor(preventResult.ascvdRisk) }}>
                    {preventResult.ascvdRisk}%
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', fontWeight: '500' }}>ASCVD</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Atherosclerotic Cardiovascular Disease
                  </Text>
                  <div style={{ marginTop: '6px' }}>
                    <Tag
                      color={getRiskColor(preventResult.ascvdRisk)}
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      {getRiskCategory(preventResult.ascvdRisk)}
                    </Tag>
                  </div>
                </div>
              </Tooltip>
            </Col>

            {/* Stroke/CVA Risk */}
            <Col xs={24} sm={12} md={8}>
              <Tooltip title="A cerebrovascular accident (CVA) is the medical term for a stroke.">
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  borderRadius: '6px',
                  border: `1px solid ${getRiskColor(preventResult.strokeRisk)}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <Text strong style={{ fontSize: '24px', color: getRiskColor(preventResult.strokeRisk) }}>
                    {preventResult.strokeRisk}%
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', fontWeight: '500' }}>CVA</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Cerebrovascular Accident (Stroke)
                  </Text>
                  <div style={{ marginTop: '6px' }}>
                    <Tag
                      color={getRiskColor(preventResult.strokeRisk)}
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      {getRiskCategory(preventResult.strokeRisk)}
                    </Tag>
                  </div>
                </div>
              </Tooltip>
            </Col>

            {/* Heart Failure/CHF Risk */}
            <Col xs={24} sm={12} md={8}>
              <Tooltip title="Congestive heart failure (CHF) is a chronic progressive condition that affects the pumping power of your heart muscles.">
                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  borderRadius: '6px',
                  border: `1px solid ${getRiskColor(preventResult.heartFailureRisk)}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <Text strong style={{ fontSize: '24px', color: getRiskColor(preventResult.heartFailureRisk) }}>
                    {preventResult.heartFailureRisk}%
                  </Text>
                  <br />
                  <Text style={{ fontSize: '14px', fontWeight: '500' }}>CHF</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Congestive Heart Failure
                  </Text>
                  <div style={{ marginTop: '6px' }}>
                    <Tag
                      color={getRiskColor(preventResult.heartFailureRisk)}
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      {getRiskCategory(preventResult.heartFailureRisk)}
                    </Tag>
                  </div>
                </div>
              </Tooltip>
            </Col>
          </Row>

          <Modal
            title="Your 10-Year CVD Risk Factors"
            open={isModalVisible}
            onCancel={() => setIsModalVisible(false)}
            footer={null}
          >
            <Row gutter={[16, 8]}>
              <Col xs={12} sm={8}>
                <div>
                  <Text><strong>BP Medications:</strong> {patientDemographics?.isOnBpMeds ? 'Yes' : 'No'}</Text>
                  <br />
                  <Text><strong>Diabetes:</strong> {patientDemographics?.hasDiabetes ? 'Yes' : 'No'}</Text>
                </div>
              </Col>
              <Col xs={12} sm={8}>
                <div>
                  <Text><strong>Smoking:</strong> {patientDemographics?.isSmoker ? 'Yes' : 'No'}</Text>
                  <br />
                  <Text><strong>Age:</strong> {preventResult.originalAge || 'N/A'} years</Text>
                </div>
              </Col>
            </Row>
          </Modal>
        </>
      )}
    </Card>
  );
};

export default PreventScore;
